import { type ReactNode, useMemo } from 'react';

import {
	AssetsIcon,
	AtlassianIcon,
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	FocusIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	TrelloIcon,
} from '@atlaskit/logo';

// This is the only place we want to import this
import {
	DO_NOT_IMPORT_NewProductsOrder3P,
	DO_NOT_IMPORT_ProductKeys3P,
	DO_NOT_IMPORT_ProductsOrder3P,
	ThirdPartyConfigs,
} from './3p-product-configs';
import { get3PLogo, type RovoLogo, RovoLogoFromLogo } from './icons';
import { AssetsSearchResultIcon } from './icons/assets';
import { AtlasSearchResultIcon } from './icons/atlas-search';
import { CompassSearchResultIcon } from './icons/compass';
import { ConfluenceSearchResultIcon } from './icons/confluence-search';
import { FocusSearchResultIcon } from './icons/focus';
import { JiraProductDiscoverySearchResultIcon } from './icons/jira-product-discovery';
import { JiraSearchResultIcon } from './icons/jira-search';
import { JiraServiceManagementSearchResultIcon } from './icons/jira-service-management';
import { TrelloSearchResultIcon } from './icons/trello';

export interface ProductConfig {
	displayName: ReactNode;
	Icon: RovoLogo;
	IconBorderless?: RovoLogo;
	is3P?: boolean;
	SearchResultIcon?: RovoLogo;
}

const ProductKeys1P = {
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Trello: 'trello',
	Focus: 'mercury',
	Assets: 'cmdb',
	// Can't add these until they're ready as we use this type to enforce that product configs are available for all products
	// Talent: 'talent',
	// Opsgenie: 'opsgenie',
} as const;

const ProductKeys1PForAnalytics = {
	...ProductKeys1P,
	Loom: 'loom',
};

export const ProductKeys3P = DO_NOT_IMPORT_ProductKeys3P;

export const ProductsOrder3P = DO_NOT_IMPORT_ProductsOrder3P;
export const NewProductsOrder3P = DO_NOT_IMPORT_NewProductsOrder3P;

export const ProductKeys = {
	...ProductKeys1P,
	...DO_NOT_IMPORT_ProductKeys3P,
} as const;

export type ProductKeys3P =
	(typeof DO_NOT_IMPORT_ProductKeys3P)[keyof typeof DO_NOT_IMPORT_ProductKeys3P];
export type ProductKeys1P = (typeof ProductKeys1P)[keyof typeof ProductKeys1P];
export type ProductKeys1PForAnalytics =
	(typeof ProductKeys1PForAnalytics)[keyof typeof ProductKeys1PForAnalytics];
export type ProductKeys = (typeof ProductKeys)[keyof typeof ProductKeys];
export const allProductKeys: ProductKeys[] = Object.values(ProductKeys);
export const all3pProductKeys: ProductKeys3P[] = Object.values(DO_NOT_IMPORT_ProductKeys3P);
export const all1pProductKeys: ProductKeys1P[] = Object.values(ProductKeys1P);
export const is1pProductKey = (key: string): key is ProductKeys1P =>
	all1pProductKeys.includes(key as ProductKeys1P);
export const is3pProductKey = (key: string): key is ProductKeys3P =>
	all3pProductKeys.includes(key as ProductKeys3P);
export const isProductKey = (key?: string): key is ProductKeys =>
	allProductKeys.includes(key as ProductKeys);

export const productsSupportingEmptyQuery: ProductKeys[] = [ProductKeys.Confluence];

/*
 * The logic here is that if a product key is not in the 1P keys, it must be a 3P key.
 * We can't use `is3pProductKey` as it will impact how we are handling connectors.
 * This is meant to only be used for analytics purposes, to differentiate between 1P and 3P products.
 * */
export const is3pProductKeyForAnalytics = (
	key: string | undefined,
): key is ProductKeys1PForAnalytics =>
	!Object.values(ProductKeys1PForAnalytics).includes(key as ProductKeys1PForAnalytics);

export const PRODUCTS_ORDER: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Compass,
	ProductKeys.Focus,
	ProductKeys.Assets,
	// ProductKeys.Talent,
	// ProductKeys.Opsgenie,

	...DO_NOT_IMPORT_ProductsOrder3P,
];

// Updated order to reflect 3P apps popularity as per https://hello.atlassian.net/wiki/x/pyVrVgE.
// TODO: QS-8075 replace into PRODUCTS_ORDER when cleaning up the FG
export const PRODUCTS_ORDER_WITH_3P_BY_POPULARITY: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Compass,
	ProductKeys.Focus,
	ProductKeys.Assets,
	// ProductKeys.Opsgenie,

	...DO_NOT_IMPORT_NewProductsOrder3P,
];

// This is different to ProductKeys, it represents products we have support for mounting Search Page in
export const PrimaryProductKeys = {
	// Fully supported as GA
	Atlas: 'townsquare',
	Confluence: 'confluence',
	// In development
	Assets: 'cmdb',
	Focus: 'mercury',
	Jira: 'jira',
	Elevate: 'elevate',
	Talent: 'talent',
} as const;
export type PrimaryProductKey = (typeof PrimaryProductKeys)[keyof typeof PrimaryProductKeys];

export const getUppercasePrimaryProductKey = (
	productKey: PrimaryProductKey | undefined,
): Uppercase<PrimaryProductKey> | undefined =>
	productKey ? (productKey.toUpperCase() as Uppercase<PrimaryProductKey>) : undefined;

/**
 * This is the source of truth for the products that are supported in Full Page Search.
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * For 3P products please see ./3p-product-configs
 */
export const products1P: Record<ProductKeys1P, ProductConfig> = {
	[ProductKeys.Confluence]: {
		displayName: 'Confluence',
		Icon: RovoLogoFromLogo(ConfluenceIcon, 'Confluence'),
		SearchResultIcon: get3PLogo(ConfluenceSearchResultIcon, 'Confluence', 'large'),
	},
	[ProductKeys.Atlas]: {
		displayName: 'Home',
		Icon: RovoLogoFromLogo(AtlassianIcon, 'Home'),
		SearchResultIcon: get3PLogo(AtlasSearchResultIcon, 'Home', 'large'),
	},
	[ProductKeys.Jira]: {
		displayName: 'Jira',
		Icon: RovoLogoFromLogo(JiraIcon, 'Jira'),
		SearchResultIcon: get3PLogo(JiraSearchResultIcon, 'Jira', 'large'),
	},
	[ProductKeys.JiraProductDiscovery]: {
		displayName: 'Jira Product Discovery',
		Icon: RovoLogoFromLogo(JiraProductDiscoveryIcon, 'Jira Product Discovery'),
		SearchResultIcon: get3PLogo(
			JiraProductDiscoverySearchResultIcon,
			'Jira Product Discovery',
			'large',
		),
	},
	[ProductKeys.JiraServiceManagement]: {
		displayName: 'Jira Service Management',
		Icon: RovoLogoFromLogo(JiraServiceManagementIcon, 'Jira Service Management'),
		SearchResultIcon: get3PLogo(
			JiraServiceManagementSearchResultIcon,
			'Jira Service Management',
			'large',
		),
	},
	[ProductKeys.Bitbucket]: {
		displayName: 'Bitbucket',
		Icon: RovoLogoFromLogo(BitbucketIcon, 'Bitbucket'),
		SearchResultIcon: RovoLogoFromLogo(BitbucketIcon, 'large'),
	},
	[ProductKeys.Trello]: {
		displayName: 'Trello',
		Icon: RovoLogoFromLogo(TrelloIcon, 'Trello'),
		SearchResultIcon: get3PLogo(TrelloSearchResultIcon, 'Trello', 'large'),
	},
	[ProductKeys.Compass]: {
		displayName: 'Compass',
		Icon: RovoLogoFromLogo(CompassIcon, 'Compass'),
		SearchResultIcon: get3PLogo(CompassSearchResultIcon, 'Compass', 'large'),
	},
	[ProductKeys.Focus]: {
		displayName: 'Focus',
		Icon: RovoLogoFromLogo(FocusIcon, 'Focus'),
		SearchResultIcon: get3PLogo(FocusSearchResultIcon, 'Focus', 'large'),
	},
	[ProductKeys.Assets]: {
		displayName: 'Assets',
		Icon: RovoLogoFromLogo(AssetsIcon, 'Assets'),
		SearchResultIcon: get3PLogo(AssetsSearchResultIcon, 'Assets', 'large'),
	},
	// [ProductKeys.Talent]: {
	// 	displayName: 'Talent',
	// 	Icon: RovoLogoFromLogo(TalentIcon, 'Talent'),
	// 	SearchResultIcon: get3PLogo(TalentSearchResultIcon, 'Talent', 'large'),
	// },
};

const getProducts3PFromConfig = (): Record<ProductKeys3P, ProductConfig> => {
	// The use of Partial, the if (config) and the cast here are intentional TS gymnastics
	// We KNOW that this returns a config for all the ProductKeys3P, so we cast that
	// The TS compiler can't know that, and we'd be forced to write UI code like:
	//
	// const Icon = configs[productKey].Icon
	// return Icon && <Icon />
	//
	// Because TS can't prove productKey can be guaranteed to index into configs. But we know it can!
	const thirdPartyProducts = ThirdPartyConfigs;
	const configs: Partial<Record<ProductKeys3P, ProductConfig>> = {};

	all3pProductKeys.map((productKey3P) => {
		const config = thirdPartyProducts.get(productKey3P);
		if (config) {
			configs[productKey3P] = {
				displayName: config.displayName,
				Icon: config.icon,
				IconBorderless: config.iconBorderless,
				is3P: true,
				...(config.searchResultIcon && { SearchResultIcon: config.searchResultIcon }),
			};
		}
	});

	return configs as Record<ProductKeys3P, ProductConfig>;
};

/**
 * This is the source of truth for the products that are supported in Full Page Search for use by the UI
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * If you just need the set of productKeys, please import { allProductKeys } or other similar helpers
 *
 * To get the list of products available to a user (a state concern) please see the exports in store/search
 */
export const useProductConfigs = (): Record<ProductKeys, ProductConfig> => {
	return useMemo((): Record<ProductKeys, ProductConfig> => {
		const thirdPartyProducts = getProducts3PFromConfig();

		return {
			...products1P,
			...thirdPartyProducts,
		};
	}, []);
};

export const useProductConfig = (productKey: ProductKeys): ProductConfig => {
	return useProductConfigs()[productKey];
};

// Used by SAIN to detrermine knowledge source filtering
enum KnowledgeSourceTypeValues {
	GITHUB = 'github',
	CONFLUENCE = 'confluence',
	JIRA = 'jira',
	ATLAS = 'atlas',
	HELP_AGGREGATOR = 'help_aggregator',
	GOOGLE_DRIVE = 'google_drive',
	MICROSOFT_SHAREPOINT = 'microsoft_sharepoint',
	SLACK = 'slack',
	MICROSOFT_TEAMS = 'teams',
	FIGMA = 'figma',
	CONFLUENCE_DC = 'confluence-dc',
	ATLASSIAN_WEB_CRAWLER = 'atlassian-web-crawler',
	LOOM = 'loom',
	GOOGLE_CALENDAR = 'google_calendar',
	ASSETS = 'cmdb',
}

export const MapProductToKnowledgeSources: { [key: string]: string } = {
	[ProductKeys.Confluence]: KnowledgeSourceTypeValues.CONFLUENCE,
	[ProductKeys.Github]: KnowledgeSourceTypeValues.GITHUB,
	[ProductKeys.Jira]: KnowledgeSourceTypeValues.JIRA,
	[ProductKeys.Atlas]: KnowledgeSourceTypeValues.ATLAS,
	[ProductKeys.GoogleDrive]: KnowledgeSourceTypeValues.GOOGLE_DRIVE,
	[ProductKeys.Sharepoint]: KnowledgeSourceTypeValues.MICROSOFT_SHAREPOINT,
	[ProductKeys.Slack]: KnowledgeSourceTypeValues.SLACK,
	[ProductKeys.Teams]: KnowledgeSourceTypeValues.MICROSOFT_TEAMS,
	[ProductKeys.Figma]: KnowledgeSourceTypeValues.FIGMA,
	[ProductKeys.ConfluenceDC]: KnowledgeSourceTypeValues.CONFLUENCE_DC,
	[ProductKeys.AtlassianWebCrawler]: KnowledgeSourceTypeValues.ATLASSIAN_WEB_CRAWLER,
	[ProductKeys.Loom]: KnowledgeSourceTypeValues.LOOM,
	[ProductKeys.GoogleCalendar]: KnowledgeSourceTypeValues.GOOGLE_CALENDAR,
	[ProductKeys.Assets]: KnowledgeSourceTypeValues.ASSETS,
};
